import { TextInput } from 'flowbite-react';
import React from 'react';

import type { AsyncStatus } from '../../../../@types/AsyncStatus';
import type { Subcontractor } from '../../../../@types/Subcontractor';
import Spinner from '../../../common/Spinner';

interface Step4AgenciesListProps {
  status: AsyncStatus;
  targetCount: number;
  subcontractors: Subcontractor[];
  siretInput: string;
  errorMessage: string | null;
  onSiretChange: (value: string) => void;
  onAddSiren: () => void;
  onDelete: (id: number) => void;
  onMarkSigned: (id: number) => void;
}

const Step4AgenciesList: React.FC<Step4AgenciesListProps> = ({
  status,
  targetCount,
  subcontractors,
  siretInput,
  errorMessage,
  onSiretChange,
  onAddSiren,
  onDelete,
  onMarkSigned,
}) => {
  const currentCount = subcontractors.length;
  const remainingCount = Math.max(0, targetCount - currentCount);
  const allSigned = subcontractors.every(s => s.cnilContractSigned);
  const isComplete = remainingCount === 0 && allSigned;

  return (
    <div className="space-y-5">
      {/* Contract information - mandatory notice */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex gap-3">
            <div className="shrink-0">
              <svg className="mt-0.5 h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-amber-800">Contrat de sous-traitance obligatoire</h4>
              <p className="mt-1 text-sm text-amber-700">
                Afin de pouvoir traiter vos transactions, vous devez faire signer le contrat de sous-traitance par toutes vos agences partenaires.
              </p>
            </div>
          </div>
          <button 
            className="flex shrink-0 items-center gap-2 rounded border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/downloads/contrat-sous-traitant.docx';
              link.download = 'contrat-sous-traitant.docx';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Télécharger le modèle de contrat
          </button>
        </div>
      </div>

      {/* Progress indicator - compact inline version */}
      <div className="flex items-center gap-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
          isComplete ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
        }`}>
          {currentCount}/{targetCount}
        </div>
        <span className={`text-sm font-medium ${isComplete ? 'text-green-700' : 'text-gray-700'}`}>
          {isComplete
            ? <><span role="img" aria-label="check mark">✓</span> Configuration terminée</>
            : remainingCount > 0
              ? `${remainingCount} agence${remainingCount > 1 ? 's' : ''} à ajouter`
              : `${subcontractors.filter(s => !s.cnilContractSigned).length} contrat${subcontractors.filter(s => !s.cnilContractSigned).length > 1 ? 's' : ''} à signer`
          }
        </span>
      </div>

      {/* Add agency form - only show if needed */}
      {remainingCount > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex-1">
              <TextInput
                type="text"
                value={siretInput}
                onChange={(e) => {
                  const sanitized = e.target.value.replaceAll(/\D/g, '').slice(0, 14);
                  onSiretChange(sanitized);
                }}
                placeholder="SIRET de l'agence (14 chiffres)"
                className="w-full"
                maxLength={14}
              />
            </div>
            <button
              className="flex items-center justify-center gap-2 rounded bg-rgpd-blue-light px-5 py-2 text-white shadow-sm transition-colors hover:bg-rgpd-blue-dark disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onAddSiren}
              disabled={status === 'PENDING' || siretInput.length !== 14}
            >
              {status === 'PENDING' ? (
                <>
                  <Spinner size="sm" />
                  <span>Vérification...</span>
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Ajouter
                </>
              )}
            </button>
          </div>
          {errorMessage && (
            <p className="mt-2 flex items-center gap-1 text-sm text-red-500">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errorMessage}
            </p>
          )}
        </div>
      )}

      {/* Empty state */}
      {currentCount === 0 && (
        <div className="py-8 text-center text-gray-500">
          <svg className="mx-auto mb-2 h-10 w-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-sm">Aucune agence ajoutée</p>
        </div>
      )}

      {/* Agencies list - compact cards */}
      {subcontractors.length > 0 && (
        <div className="space-y-3">
          {subcontractors.map((subcontractor) => (
            <div 
              key={subcontractor.id} 
              className={`rounded-lg border bg-white p-4 transition-all ${
                subcontractor.cnilContractSigned 
                  ? 'border-green-200' 
                  : 'border-amber-200 bg-amber-50/30'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                {/* Left: Agency info */}
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h4 className="truncate font-semibold text-gray-900">{subcontractor.name}</h4>
                    {subcontractor.cnilContractSigned && (
                      <span className="inline-flex shrink-0 items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Signé
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                    <span>SIRET: {subcontractor.siret}</span>
                    {subcontractor.address?.city && (
                      <span>{subcontractor.address.postalCode} {subcontractor.address.city}</span>
                    )}
                  </div>
                </div>
                
                {/* Right: Actions */}
                <div className="flex shrink-0 items-center gap-2">
                  {!subcontractor.cnilContractSigned && (
                    <button 
                      className="rounded bg-rgpd-blue-light px-3 py-1.5 text-sm text-white transition-colors hover:bg-rgpd-blue-dark"
                      onClick={() => onMarkSigned(subcontractor.id)}
                    >
                      Marquer signé
                    </button>
                  )}
                  <button
                    onClick={() => onDelete(subcontractor.id)}
                    className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                    title="Supprimer"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Step4AgenciesList;


